import { useTranslation } from 'react-i18next';
import { toastr } from 'react-redux-toastr';
import { useDispatch } from 'react-redux';
import { pendingTask, begin, end } from 'react-redux-spinner';
import { publishBid, cancelBid, refreshBidDashboard } from '../../store/bidProfileManagement/bidProfileManagement.middlewares';
import { openPublishProgressSidePanel } from '../../store/workplanPublishProgress';
import { LOADING_START, LOADING_END } from '../../../store/loading';
import * as bidProfileService from '../../services/api-endpoints/bidProfile.service';
import { ManagerBidAction, BidProfileEditTab } from '../../../Enums';

/**
 * @param {string} profileId
 * @param {boolean} isPublishingToWorkplan
 * @param {{
 *  bidProfiles: BidProfile.IBriefBaseProfile[],
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
*/
const useBidProfileSplitButton = (profileId, isPublishingToWorkplan, storeData) => {
    const dispatch = useDispatch();
    const { t: tPrompt } = useTranslation('shiftBidding', { keyPrefix: 'manager.bidProfileEdit.bidDashboard.prompt' });

    const signOffOrCancel = async (/** @type {number} */ bidAction) => dispatch((bidAction === ManagerBidAction.Publish
        ? publishBid : cancelBid)(profileId, storeData));

    const executeAction = async (bidAction) => {        
        if (bidAction === ManagerBidAction.ForceStart) {
            try {
                dispatch({ type: LOADING_START, [pendingTask]: begin });
                await bidProfileService.activateBid(profileId);
                toastr.success(
                    tPrompt('success'),
                    tPrompt('forceStartBidSuccess'),
                );
                // Refresh the bid profile data after successful action
                dispatch(refreshBidDashboard(profileId, storeData, BidProfileEditTab.Dashboard));
            } catch (error) {
                toastr.error(
                    tPrompt('error'),
                    tPrompt('forceStartBidError'),
                );
                console.error('Force Start Bid error:', error);
            } finally {
                dispatch({ type: LOADING_END, [pendingTask]: end });
            }
        } else if (bidAction === ManagerBidAction.ForceClose) {
            try {
                dispatch({ type: LOADING_START, [pendingTask]: begin });
                await bidProfileService.closeBid(profileId);
                toastr.success(
                    tPrompt('success'),
                    tPrompt('forceCloseBidSuccess'),
                );
                // Refresh the bid profile data after successful action
                dispatch(refreshBidDashboard(profileId, storeData, BidProfileEditTab.Dashboard));
            } catch (error) {
                toastr.error(
                    tPrompt('error'),
                    tPrompt('forceCloseBidError'),
                );
                console.error('Force Close Bid error:', error);
            } finally {
                dispatch({ type: LOADING_END, [pendingTask]: end });
            }
        } else {
            return signOffOrCancel(bidAction);
        }
    };

    const getPromptKey = (bidAction) => {
        switch (bidAction) {
            case ManagerBidAction.Publish:
                return 'publishBid';
            case ManagerBidAction.Cancel:
                return 'cancelBid';
            case ManagerBidAction.ForceStart:
                return 'forceStartBid';
            case ManagerBidAction.ForceClose:
                return 'forceCloseBid';
            default:
                return 'publishBid';
        }
    };

    /**
     * @param {{value: number}} param0
     */
    const signOffAndPublish = ({ value }) => {
        if (isPublishingToWorkplan || value === ManagerBidAction.RolloutStatusDetails) {
            dispatch(openPublishProgressSidePanel(profileId));
            return;
        }
        
        toastr.confirm(
            tPrompt(getPromptKey(value)),
            {
                onOk: () => executeAction(value),
                onCancel: () => { },
            },
        );
    };
    return signOffAndPublish;
};

export default useBidProfileSplitButton;
